import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, CalendarDays, Zap } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  const [selectedOption, setSelectedOption] = useState<'postural' | 'estetica'>('postural');
  return <section className="relative min-h-screen flex overflow-hidden pt-20" data-id="element-183">
      {/* Full-width Background Image */}
      <div className="absolute inset-0 z-0 bg-[#F0F2F5]" data-id="element-184">
        <img src="https://cdn.magicpatterns.com/uploads/3wqE8xVUNEod2tku7yed8e/AdobeStock_1873241795.jpg" alt="Spine" className="w-full h-full object-cover object-right translate-x-[0%] md:translate-x-[15%] scale-110" data-id="element-185" />
      </div>

      {/* Light gray overlay just behind the text for readability */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#F0F2F5] via-[#F0F2F5]/90 to-transparent w-full md:w-5/6 lg:w-3/4" data-id="element-186" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center pt-40 pb-20" data-id="element-187">
        <div className="relative z-10 mt-12 lg:mt-20 max-w-3xl" data-id="element-188">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-189">
            {/* Badge */}
            <div className="inline-flex items-center px-5 py-2.5 rounded-full border border-gray-300 backdrop-blur-sm bg-white/30 mb-8" data-id="element-190">
              <Zap className="w-3.5 h-3.5 text-bodyon-teal mr-2" fill="currentColor" data-id="element-191" />
              <span className="text-gray-600 font-medium tracking-[0.2em] uppercase text-xs" data-id="element-192">
                {t('hero.badge')}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-heading font-semibold text-4xl md:text-5xl lg:text-[3.5rem] xl:text-[4rem] leading-[1.5] tracking-[-0.02em] mb-8" data-id="element-193">
              <span className="block text-bodyon-teal-dark md:whitespace-nowrap pb-2" data-id="element-194">
                {t('hero.title1')}
              </span>
              <span className="block text-bodyon-teal-dark md:whitespace-nowrap pb-2" data-id="element-195">
                {t('hero.title2')}
              </span>
              <span className="block text-bodyon-teal-dark md:whitespace-nowrap" data-id="element-196">
                {t('hero.title3')}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-bodyon-teal font-semibold text-xl md:text-2xl lg:text-3xl leading-snug mb-6" data-id="element-197">
              <span className="block md:whitespace-nowrap" data-id="element-198">
                {t('hero.subtitle1')}
              </span>
              <span className="block md:whitespace-nowrap" data-id="element-199">
                {t('hero.subtitle2')}
              </span>
            </p>

            {/* Description */}
            <p className="text-base md:text-lg text-gray-500 max-w-3xl leading-relaxed font-light mb-10" data-id="element-200">
              <span className="block md:whitespace-nowrap" data-id="element-201">
                {t('hero.desc1_start')}
                <span className="font-semibold" data-id="element-202">{t('hero.desc1_bold')}</span>
                {t('hero.desc1_end')}
              </span>
              <span className="block md:whitespace-nowrap" data-id="element-203">
                {t('hero.desc2')}
              </span>
              <span className="block md:whitespace-nowrap" data-id="element-204">
                {t('hero.desc3')}
              </span>
            </p>
          </motion.div>

          {/* Interactive Switch CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: 'easeOut'
        }} className="bg-white/40 backdrop-blur-xl border border-white/60 p-8 rounded-[2rem] shadow-[0_8px_30px_rgba(0,0,0,0.03)] max-w-2xl relative overflow-hidden" data-id="element-205">
            {/* Subtle glow effect behind the card */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-bodyon-teal/10 rounded-full blur-3xl pointer-events-none" data-id="element-206" />

            <h3 className="text-sm font-semibold text-bodyon-teal-dark uppercase tracking-widest mb-6 flex items-center gap-2" data-id="element-207">
              <CalendarDays className="w-5 h-5 text-bodyon-teal" strokeWidth={2.5} data-id="element-208" />
              AGENDAR CONSULTA
            </h3>

            <div className="relative flex bg-gray-50/80 border border-gray-100/50 rounded-full p-1.5 mb-8" data-id="element-209">
              <button onClick={() => setSelectedOption('postural')} className={`relative flex-1 py-3.5 px-4 text-sm font-semibold rounded-full transition-colors z-10 whitespace-nowrap ${selectedOption === 'postural' ? 'text-bodyon-teal' : 'text-gray-500 hover:text-gray-800'}`} data-id="element-210">
                {selectedOption === 'postural' && <motion.div layoutId="active-pill" className="absolute inset-0 bg-white shadow-sm rounded-full -z-10" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-211" />}
                <span className="relative z-10" data-id="element-212">
                  Evaluación Postural/Movilidad
                </span>
              </button>

              <button onClick={() => setSelectedOption('estetica')} className={`relative flex-1 py-3.5 px-4 text-sm font-semibold rounded-full transition-colors z-10 whitespace-nowrap ${selectedOption === 'estetica' ? 'text-bodyon-teal' : 'text-gray-500 hover:text-gray-800'}`} data-id="element-213">
                {selectedOption === 'estetica' && <motion.div layoutId="active-pill" className="absolute inset-0 bg-white shadow-sm rounded-full -z-10" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-214" />}
                <span className="relative z-10" data-id="element-215">Estética Regenerativa</span>
              </button>
            </div>

            <a href={`https://wa.me/?text=Hola,%20me%20interesa%20agendar%20una%20${selectedOption === 'postural' ? 'Evaluación%20Postural%20y%20de%20Movilidad' : 'Consulta%20de%20Estética%20Regenerativa'}`} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between w-full bg-bodyon-teal hover:bg-bodyon-teal-dark text-white pl-6 pr-2 py-2 rounded-full transition-all duration-500 font-semibold" data-id="element-216">
              <span className="flex items-center gap-2" data-id="element-217">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" data-id="element-218">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-219" />
                </svg>
                Continuar por WhatsApp
              </span>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-bodyon-teal-dark transition-all duration-700 ease-out" data-id="element-220">
                <ArrowRight className="w-5 h-5" data-id="element-221" />
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}